import { supabase } from '@/lib/supabase';
import type { System, AdminAllowUser, AdminFeatureFlag } from '@/types';

export const settingsApi = {
  listSystems: async (): Promise<System[]> => {
    const { data, error } = await supabase
      .from('systems')
      .select('*')
      .order('sort_order')
      .order('name');
    if (error) throw error;
    return (data ?? []) as System[];
  },

  createSystem: async (system: Omit<System, 'id' | 'created_at'>): Promise<System> => {
    const { data, error } = await supabase
      .from('systems')
      .insert(system)
      .select()
      .single();
    if (error) throw error;
    return data as System;
  },

  updateSystem: async (id: string, patch: Partial<System>): Promise<System> => {
    const { data, error } = await supabase
      .from('systems')
      .update(patch)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return data as System;
  },

  deleteSystem: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('systems')
      .delete()
      .eq('id', id);
    if (error) throw error;
  },

  listAdminUsers: async (): Promise<AdminAllowUser[]> => {
    const { data, error } = await supabase
      .from('admin_users')
      .select('*')
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data ?? []) as AdminAllowUser[];
  },

  addAdminUser: async (email: string): Promise<AdminAllowUser> => {
    const normalized = email.trim().toLowerCase();
    const { data, error } = await supabase
      .from('admin_users')
      .upsert({ email: normalized }, { onConflict: 'email' })
      .select()
      .single();
    if (error) throw error;
    return data as AdminAllowUser;
  },

  removeAdminUser: async (email: string): Promise<void> => {
    const { error } = await supabase
      .from('admin_users')
      .delete()
      .eq('email', email);
    if (error) throw error;
  },

  listFeatureFlags: async (): Promise<AdminFeatureFlag[]> => {
    const { data, error } = await supabase
      .from('admin_feature_flags')
      .select('*')
      .order('key');
    if (error) throw error;
    return (data ?? []) as AdminFeatureFlag[];
  },

  upsertFeatureFlag: async (
    key: string,
    value: AdminFeatureFlag['value'],
  ): Promise<AdminFeatureFlag> => {
    const { data, error } = await supabase
      .from('admin_feature_flags')
      .upsert(
        {
          key,
          value,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'key' }
      )
      .select()
      .single();
    if (error) throw error;
    return data as AdminFeatureFlag;
  },

  deleteFeatureFlag: async (key: string): Promise<void> => {
    const { error } = await supabase
      .from('admin_feature_flags')
      .delete()
      .eq('key', key);
    if (error) throw error;
  },
};
